/**
 * The documents browser's data hooks (#6559): the paged search, the folder
 * rows for the browse listing, the People facet, the organization's document
 * types, the Locked count for the view switch, and the record reads the
 * destinations column and the records index are built on.
 *
 * Every key starts with the file tree's root key, so the invalidation an
 * upload, delete or move already performs on the tree refreshes the browser
 * too — one cache family for one filesystem.
 */
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { BASE_API_URL_V2 } from "@/constants";
import fpFetch from "@/services/fpFetch";
import { useViewerTimeZone } from "@/hooks/useViewerTimeZone";
import type { FolderEntry } from "@/hooks/useFileTreeV2/fetchFiles";
import {
  DocumentFoldersSearchRequest,
  DocumentRecord,
  DocumentsSearchRequest,
  DocumentStatusBucket,
  DocumentUploader,
  SuggestedDocument,
  fetchDocumentRecord,
  fetchDocumentUploaders,
  fetchRecordSummaries,
  fetchRecordsEnabled,
  fetchFolderRecord,
  fetchPlaces,
  fetchRecordCounts,
  fetchRecordsIndex,
  fetchSuggestedDocuments,
  searchDocumentFolders,
  searchDocuments,
} from "./api";
import type {
  Places,
  RecordCounts,
  RecordSummary,
  RecordsIndex,
  RecordsIndexRequest,
} from "./api";
import {
  BrowserState,
  buildSearchRequest,
  effectivePath,
  hasActiveFilterValues,
  isRecursive,
  keepsRowsAcross,
  listingBesideTheRun,
} from "./state";
import type { SearchScopeRequest } from "./state";

export const DOCUMENTS_BROWSER_KEY = ["fileTree", "documentsBrowser"] as const;

export interface DocumentTypeOption {
  key: string;
  label: string;
}

export interface SearchOptions {
  driveId?: string | null;
  /** Where a narrowed browse listing looks (round 17): the scope chip's. */
  scope?: SearchScopeRequest;
  excludeBlobKinds?: string[];
  pageSize?: number;
  /** False keeps the query idle — a surface that renders none of its rows
   *  (Home) should not fetch them. */
  enabled?: boolean;
  /** A fixed set of documents — the picker's "Show selected" (round 10). */
  blobIds?: string[];
  /** False asks for no total (#12073): Home's listing shows none, and the
   *  total is the whole cost of the request on a large organization. */
  withTotal?: boolean;
  /** The portals view's listed portals (#15170); see `RequestOptions`. */
  portalKeys?: string[];
}

/** The paged search behind the table. The request is rebuilt from the state
 *  on every page so a preset like "today" is re-resolved rather than frozen
 *  at first render. */
export const useDocumentsSearch = (
  state: BrowserState,
  options: SearchOptions = {}
) => {
  const zone = useViewerTimeZone();
  const build = useCallback(
    (cursor: string | null): DocumentsSearchRequest =>
      buildSearchRequest(state, {
        now: new Date(),
        zone,
        driveId: options.driveId,
        scope: options.scope,
        excludeBlobKinds: options.excludeBlobKinds,
        pageSize: options.pageSize,
        cursor,
        blobIds: options.blobIds,
        withTotal: options.withTotal,
        portalKeys: options.portalKeys,
      }),
    [
      state,
      zone,
      options.driveId,
      options.scope,
      options.excludeBlobKinds,
      options.pageSize,
      options.blobIds,
      options.withTotal,
      options.portalKeys,
    ]
  );
  // The first page's request, minus the volatile bits, is the cache identity.
  const identity = useMemo(() => {
    const first = build(null);
    return JSON.stringify(first);
  }, [build]);

  // A service's listing apart from its sync narrowing (#11635 slice 1, R52):
  // when only the narrowing changed, the previous rows stand in until the
  // next answer lands, rather than the skeleton. It rides last in the key
  // so the previous query's key says what that listing was.
  const besideTheRun = listingBesideTheRun(state);
  const query = useInfiniteQuery({
    queryKey: [...DOCUMENTS_BROWSER_KEY, "search", identity, besideTheRun],
    queryFn: ({ pageParam }) => searchDocuments(build(pageParam)),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 30_000,
    enabled: options.enabled ?? true,
    placeholderData: (previousData, previousQuery) => {
      const previous = previousQuery?.queryKey[4];
      return keepsRowsAcross(
        typeof previous === "string" ? previous : null,
        besideTheRun
      )
        ? previousData
        : undefined;
    },
  });

  const rows = useMemo(
    () => query.data?.pages.flatMap((page) => page.files) ?? [],
    [query.data]
  );
  const total = query.data?.pages[0]?.total ?? null;
  return { ...query, rows, total };
};

/** A folder row with what the tree needs to draw it: whether it has folders
 *  of its own, so a caret is offered only where it opens something. */
export interface FolderNode extends FolderEntry {
  hasChildren: boolean;
}

/** A term that can search folder names (#12088): one carrying a word of
 *  three letters or digits, the shortest thing the path's trigram index
 *  can narrow on. The length of the whole term says nothing — "JP M" is
 *  four characters and narrows nothing — so the test is on a word, as the
 *  route's is. */
export const FOLDER_HITS_WORD = /[a-zA-Z0-9]{3}/;
/** How many folder hits a search lists: an offer beside the paged
 *  documents, not a paged listing of its own. */
export const FOLDER_HITS_LIMIT = 10;

/** Whether a listing lists the folders its term names (#12088): a term
 *  that can search the path, in a browse view or from Home — a search from
 *  Home is a search over everything (a named view lists what it names; a
 *  record location lists the record's documents) — with no chip
 *  beside it — folders carry no type, uploader, date or record, so a chip
 *  would narrow the documents and not the folders. The Location chip is the
 *  search's scope rather than a narrowing, and the hits honour it. */
export const wantsFolderHits = (state: BrowserState): boolean =>
  (state.view === "all" || state.view === "drives" || state.view === "home") &&
  FOLDER_HITS_WORD.test(state.q) &&
  !hasActiveFilterValues({ ...state.filters, location: null });

/** Folder rows for the listing: a browse listing's are the folder's own
 *  children, from the tree's route; a search's are the folders its term
 *  names (#12088), wherever they are within the search's scope. */
export const useFolderRows = (
  state: BrowserState,
  options: SearchOptions = {}
) => {
  const enabled = options.enabled ?? true;
  // A fixed set of ids lists no folders: it is recursive by definition.
  const browsing = enabled && !isRecursive(state) && !options.blobIds;
  const searching =
    enabled && !options.blobIds && isRecursive(state) && wantsFolderHits(state);
  const children = useFolderChildren(effectivePath(state), browsing, options);
  const hits = useFolderHits(state, searching, options);
  // Whether the listing still waits on its folder rows (round 9): only a
  // browse listing or a search asks for them, and a disabled query is
  // pending forever, so the two facts live together here rather than being
  // mirrored by hand. `isPending` rather than `isLoading` so a retrying
  // request blocks too — the hits query retries nothing, so a failed offer
  // releases the documents at once rather than holding them through a
  // retry window. Only what the browser reads: spreading the result would
  // subscribe it to every field the query tracks.
  return {
    data: browsing ? children.data : searching ? hits.data : undefined,
    blocking: (browsing && children.isPending) || (searching && hits.isPending),
  };
};

/** The folders a search's term names (#12088), under the search's scope —
 *  the same folder, drive, roots and excluded kinds the documents request
 *  carries, so a hit is always a folder the listing could reach. */
const useFolderHits = (
  state: BrowserState,
  enabled: boolean,
  options: SearchOptions = {}
) => {
  const zone = useViewerTimeZone();
  const request = useMemo(() => {
    // The scope is read off the documents request so the two never
    // disagree; the request's date bounds are the only part that depends on
    // the clock, and the hits take none of them.
    const search = buildSearchRequest(state, {
      now: new Date(),
      zone,
      driveId: options.driveId,
      scope: options.scope,
      excludeBlobKinds: options.excludeBlobKinds,
    });
    const body: DocumentFoldersSearchRequest = {
      q: search.q ?? "",
      limit: FOLDER_HITS_LIMIT,
    };
    // Only a scoped search names a folder; unscoped is the field's absence.
    if (search.path?.length) body.path = search.path;
    if (search.drive_id) body.drive_id = search.drive_id;
    if (search.roots) body.roots = search.roots;
    if (search.drive_ids) body.drive_ids = search.drive_ids;
    if (search.exclude_blob_kinds)
      body.exclude_blob_kinds = search.exclude_blob_kinds;
    return body;
  }, [state, zone, options.driveId, options.scope, options.excludeBlobKinds]);
  return useQuery({
    queryKey: [...DOCUMENTS_BROWSER_KEY, "folderHits", JSON.stringify(request)],
    enabled,
    staleTime: 30_000,
    // An offer beside the documents, never a gate on them: a failed request
    // is a listing with no folder rows, not a listing held through the
    // client's retry backoff (the browser waits while this is pending).
    retry: false,
    queryFn: async (): Promise<FolderNode[]> =>
      // A hit is drawn as a row, never as a tree node: whether it opens
      // onto more folders is not asked.
      (await searchDocumentFolders(request)).map((folder) => ({
        ...folder,
        hasChildren: false,
      })),
  });
};

/** The folders directly under `path` — the browse listing's folder rows and
 *  the destinations tree's children — read two levels deep so each child
 *  already knows whether it has folders of its own. */
export const useFolderChildren = (
  path: string[],
  enabled: boolean,
  options: SearchOptions = {}
) => {
  const zone = useViewerTimeZone();
  return useQuery({
    queryKey: [
      ...DOCUMENTS_BROWSER_KEY,
      "folders",
      options.driveId ?? null,
      JSON.stringify(path),
      zone,
    ],
    enabled,
    staleTime: 30_000,
    queryFn: async (): Promise<FolderNode[]> => {
      const url = new URL("/api/v2/users/files", BASE_API_URL_V2);
      if (options.driveId) url.searchParams.append("drive_id", options.driveId);
      for (const segment of path)
        url.searchParams.append("path_prefix", segment);
      // Two levels: the children, and enough of the grandchildren to know
      // which children open onto anything.
      url.searchParams.append("max_depth", "2");
      // Folders only: the browser reads its files from the search route. A
      // file depth of zero is the route's "no file rows" (#11322).
      url.searchParams.append("file_max_depth", "0");
      url.searchParams.append("user_tz", zone);
      for (const kind of options.excludeBlobKinds ?? []) {
        url.searchParams.append("exclude_blob_kinds", kind);
      }
      const response = await fpFetch(url.toString(), {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      if (!response?.ok) {
        throw new Error(
          `Folder listing failed with status ${response?.status}`
        );
      }
      const data = (await response.json()) as { folders: FolderEntry[] };
      // The route also returns the prefix folder itself (its date rolls up
      // from the listing) and the grandchildren; only the children are rows
      // here, each told whether a grandchild sits under it.
      const parents = new Set(
        data.folders
          .filter((f) => f.filePath.length === path.length + 2)
          .map((f) => JSON.stringify(f.filePath.slice(0, path.length + 1)))
      );
      return data.folders
        .filter((f) => f.filePath.length === path.length + 1)
        .map((f) => ({
          ...f,
          hasChildren: parents.has(JSON.stringify(f.filePath)),
        }))
        .sort((a, b) =>
          (a.filePath[a.filePath.length - 1] ?? "").localeCompare(
            b.filePath[b.filePath.length - 1] ?? "",
            undefined,
            { sensitivity: "base" }
          )
        );
    },
  });
};

/** The People chip's options. The read walks every document the viewer can
 *  see, the most expensive read the page makes on a large organization
 *  (#12073), so it runs only when something is about to show a name: the
 *  chip opening, or a filter already naming a person. */
export const useDocumentUploaders = (enabled: boolean) =>
  useQuery<DocumentUploader[]>({
    queryKey: [...DOCUMENTS_BROWSER_KEY, "uploaders"],
    queryFn: fetchDocumentUploaders,
    staleTime: 60_000,
    enabled,
  });

export const useDocumentTypeOptions = () =>
  useQuery<DocumentTypeOption[]>({
    queryKey: [...DOCUMENTS_BROWSER_KEY, "documentTypes"],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const response = await fpFetch(
        `${BASE_API_URL_V2}/api/v2/rms/document-config`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );
      if (!response?.ok) {
        throw new Error(
          `Document config failed with status ${response?.status}`
        );
      }
      const data = (await response.json()) as {
        document_types: Array<{ type_key: string; display_name: string }>;
      };
      return data.document_types.map((t) => ({
        key: t.type_key,
        label: t.display_name,
      }));
    },
  });

/** How many documents sit in a status bucket: a one-row search whose total
 *  is the count. The destinations show it for Locked (so a client can watch
 *  it fall) and Processing. */
export const useStatusCount = (
  bucket: DocumentStatusBucket,
  options: SearchOptions = {}
) =>
  useQuery({
    queryKey: [
      ...DOCUMENTS_BROWSER_KEY,
      "statusCount",
      bucket,
      options.driveId ?? null,
      JSON.stringify(options.excludeBlobKinds ?? []),
    ],
    staleTime: 30_000,
    enabled: options.enabled ?? true,
    queryFn: async () => {
      const page = await searchDocuments({
        recursive: true,
        status: [bucket],
        sort_by: "uploaded_at",
        sort_order: "desc",
        page_size: 1,
        ...(options.driveId ? { drive_id: options.driveId } : {}),
        ...(options.excludeBlobKinds?.length
          ? { exclude_blob_kinds: options.excludeBlobKinds }
          : {}),
      });
      return page.total;
    },
  });

/** Home's suggestions: what the viewer opened, uploaded, attached or had
 *  cited recently, with the reason. */
export const useSuggestedDocuments = (limit: number) =>
  useQuery<SuggestedDocument[]>({
    queryKey: [...DOCUMENTS_BROWSER_KEY, "suggested", limit],
    queryFn: () => fetchSuggestedDocuments(limit),
    staleTime: 30_000,
  });

/** The record a record view is looking at: its kind and name for the heading
 *  and, for a manager, the funds the roll-up chip adds. Idle without an id. */
export const useDocumentRecord = (publicId: string | null) =>
  useQuery<DocumentRecord>({
    queryKey: [...DOCUMENTS_BROWSER_KEY, "record", publicId],
    queryFn: () => {
      if (publicId === null) throw new Error("No record to resolve");
      return fetchDocumentRecord(publicId);
    },
    enabled: publicId !== null,
    staleTime: 5 * 60_000,
  });

/** Whether records are a destination in this organization at all (#6559
 *  round 7): whether it has linked anything at all. The round-6 coverage floor
 *  is gone — it was decided on a count that read as ~0% everywhere, and a
 *  share threshold needs a re-decision every time coverage moves. */
export const useRecordsEnabled = (enabled: boolean) =>
  useQuery<boolean>({
    queryKey: [...DOCUMENTS_BROWSER_KEY, "recordsEnabled"],
    queryFn: fetchRecordsEnabled,
    enabled,
    staleTime: 30 * 60_000,
  });

/** True only once the answer has arrived, so an organization that has records
 *  gains the destination a beat late rather than everyone seeing it flash
 *  away. */
export const recordsAreADestination = (enabled: boolean | undefined): boolean =>
  enabled === true;

/** The record a folder is named for. Idle outside a folder listing: the
 *  handoff is a thing you meet by standing somewhere (#6559 round 7). */
export const useFolderRecord = (path: string[] | null) =>
  useQuery<RecordSummary | null>({
    queryKey: [...DOCUMENTS_BROWSER_KEY, "folderRecord", path],
    queryFn: () => {
      if (path === null) throw new Error("No folder to resolve");
      return fetchFolderRecord(path);
    },
    enabled: path !== null && path.length > 0,
    staleTime: 5 * 60_000,
  });

/** The recents the viewer keeps, resolved to what the column and Home show
 *  (round 15). Keyed on the ids in order, so a new recent re-resolves the
 *  list; an empty list asks nothing. */
export const useRecordSummaries = (publicIds: string[], enabled: boolean) =>
  useQuery<RecordSummary[]>({
    queryKey: [...DOCUMENTS_BROWSER_KEY, "recordSummaries", publicIds],
    queryFn: () => fetchRecordSummaries(publicIds),
    enabled: enabled && publicIds.length > 0,
    staleTime: 60_000,
  });

export const useRecordsIndex = (
  request: RecordsIndexRequest,
  enabled: boolean
) =>
  useQuery<RecordsIndex>({
    queryKey: [
      ...DOCUMENTS_BROWSER_KEY,
      "recordsIndex",
      request.q ?? "",
      request.kind ?? "",
      request.sort?.key ?? "",
      request.sort?.direction ?? "",
      // The search strip asks for three rows and the index for two hundred:
      // without the size in the key the strip's answer serves the index.
      request.limit ?? 0,
      request.offset ?? 0,
    ],
    queryFn: () => fetchRecordsIndex(request),
    enabled,
    staleTime: 60_000,
  });

/** The places the column lists (round 17): one read per organization, held
 *  a while since drives and connections change by the day, not the minute. */
export const usePlaces = (enabled: boolean) =>
  useQuery<Places>({
    queryKey: [...DOCUMENTS_BROWSER_KEY, "places"],
    queryFn: fetchPlaces,
    enabled,
    staleTime: 300_000,
  });

/** The counts behind the Records page's lede and segmented kinds (round 16):
 *  keyed by the term alone, so a kind switch or a page turn re-reads
 *  nothing. */
export const useRecordCounts = (q: string) =>
  useQuery<RecordCounts>({
    queryKey: [...DOCUMENTS_BROWSER_KEY, "recordCounts", q],
    queryFn: () => fetchRecordCounts(q),
    staleTime: 60_000,
  });
